import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Copy, Mail, Download, Trash2, ShieldCheck, Lock, CheckCircle2, Clock,
  Fingerprint, Globe, Loader2, ExternalLink, BellRing, BellOff, MessageSquare,
} from "lucide-react";
import Navbar from "../components/Navbar";
import { BrandBadge } from "../components/CardBrand";
import api, { API } from "../lib/api";

export default function FormDetail() {
  const { formId } = useParams();
  const navigate = useNavigate();
  const [f, setF] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [texting, setTexting] = useState(false);
  const [smsEnabled, setSmsEnabled] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [emailMsg, setEmailMsg] = useState("");
  const [smsMsg, setSmsMsg] = useState("");
  const [showEmailEdit, setShowEmailEdit] = useState(false);
  const [showSmsEdit, setShowSmsEdit] = useState(false);

  useEffect(() => { api.get("/config").then((r) => setSmsEnabled(!!r.data.sms_enabled)).catch(() => {}); }, []);
  useEffect(() => {
    api.get("/message-settings").then((r) => {
      setEmailMsg(r.data.email_message || r.data.default_email_message);
      setSmsMsg(r.data.sms_message || r.data.default_sms_message);
    }).catch(() => {});
  }, []);

  const sendSms = async () => {
    setTexting(true);
    try {
      await api.post(`/forms/${formId}/send-sms`, { message: smsMsg });
      toast.success(`Signing link texted to ${f.customer_phone}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send SMS");
    } finally {
      setTexting(false);
    }
  };

  const signUrl = f ? `${window.location.origin}/sign/${f.token}` : "";
  const money = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n || 0);

  const load = async () => {
    try {
      const res = await api.get(`/forms/${formId}`);
      setF(res.data);
    } catch (e) {
      toast.error("Form not found");
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [formId]);

  const copyLink = () => {
    navigator.clipboard.writeText(signUrl);
    toast.success("Signing link copied");
  };

  const sendEmail = async () => {
    if (!f.customer_email) {
      toast.error("Add a customer email to this form first");
      return;
    }
    setSending(true);
    try {
      await api.post(`/forms/${formId}/send-email`, { message: emailMsg });
      toast.success(`Signing link emailed to ${f.customer_email}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send email");
    } finally {
      setSending(false);
    }
  };

  const toggleReminders = async () => {
    try {
      const res = await api.patch(`/forms/${formId}/reminders`, { enabled: !f.reminders_enabled });
      setF({ ...f, reminders_enabled: res.data.reminders_enabled });
      toast.success(res.data.reminders_enabled ? "Auto-reminders on — every 2 days until signed" : "Auto-reminders paused");
    } catch (e) {
      toast.error("Failed to update reminders");
    }
  };

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/forms/${formId}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `authorization-${formId}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Failed to generate PDF");
    } finally {
      setDownloading(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Delete this authorization form? This cannot be undone.")) return;
    try {
      await api.delete(`/forms/${formId}`);
      toast.success("Form deleted");
      navigate("/dashboard");
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  if (loading || !f) {
    return (
      <div className="min-h-screen bg-[#F8FAFC]">
        <Navbar />
        <div className="p-16 flex justify-center">
          <div className="h-8 w-8 rounded-full border-2 border-[#E2E8F0] border-t-[#2563EB] animate-spin" />
        </div>
      </div>
    );
  }

  const signed = f.status === "signed";

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Navbar />
      <main className="max-w-5xl mx-auto px-5 py-8">
        <button onClick={() => navigate("/dashboard")} className="inline-flex items-center gap-1.5 text-sm font-medium text-[#64748B] hover:text-[#0F172A] mb-5">
          <ArrowLeft size={16} /> Back to dashboard
        </button>

        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-extrabold tracking-tight text-[#0F172A]">{f.customer_name}</h1>
              <span
                className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold"
                style={signed ? { background: "#ECFDF5", color: "#059669" } : { background: "#FFFBEB", color: "#D97706" }}
              >
                {signed ? <CheckCircle2 size={13} /> : <Clock size={13} />}
                {signed ? "Signed" : "Pending signature"}
              </span>
            </div>
            <p className="text-sm text-[#64748B] mt-1">{f.merchant_name} · {money(f.amount)} · {f.frequency}</p>
          </div>
          <div className="flex items-center gap-2">
            {signed && (
              <button data-testid="download-pdf-btn" onClick={downloadPdf} disabled={downloading} className="inline-flex items-center gap-2 rounded-lg bg-[#0F172A] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#1E293B] transition-all active:scale-[0.98] disabled:opacity-60">
                {downloading ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />} PDF
              </button>
            )}
            <button data-testid="delete-form-btn" onClick={remove} className="rounded-lg border border-[#E2E8F0] p-2.5 text-[#94A3B8] hover:text-[#DC2626] hover:border-[#FCA5A5] transition-colors">
              <Trash2 size={16} />
            </button>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6 mt-6">
          {/* Main document */}
          <div className="lg:col-span-2 space-y-6">
            <div className="rounded-xl border border-[#E2E8F0] bg-white overflow-hidden">
              <div className="bg-[#0F172A] px-6 py-5 text-white flex items-center gap-2">
                <ShieldCheck size={18} className="text-[#38BDF8]" />
                <span className="font-bold">Authorization details</span>
              </div>
              <div className="p-6 grid sm:grid-cols-2 gap-x-6 gap-y-4 text-sm">
                <Row label="Customer" value={f.customer_name} />
                <Row label="Customer email" value={f.customer_email || "—"} />
                <Row label="Customer mobile" value={f.customer_phone || "—"} />
                <Row label="Merchant" value={f.merchant_name} />
                <Row label="Descriptor" value={f.merchant_descriptor || "—"} />
                <Row label="Amount" value={<span className="font-mono">{money(f.amount)}</span>} />
                <Row label="Frequency" value={<span className="capitalize">{f.frequency}{f.frequency_note ? ` · ${f.frequency_note}` : ""}</span>} />
                {f.description && <Row label="Description" value={f.description} full />}
              </div>
            </div>

            {signed ? (
              <div className="rounded-xl border border-[#E2E8F0] bg-white overflow-hidden">
                <div className="px-6 py-4 border-b border-[#E2E8F0] flex items-center gap-2">
                  <CheckCircle2 size={16} className="text-[#059669]" />
                  <span className="font-bold text-[#0F172A]">Signed authorization</span>
                </div>
                <div className="p-6 space-y-5">
                  <div className="grid sm:grid-cols-2 gap-x-6 gap-y-4 text-sm">
                    <Row label="Cardholder" value={f.card?.cardholder_name || "—"} />
                    <Row label="Card" value={<span className="inline-flex items-center gap-2"><BrandBadge brand={f.card?.brand} /><span className="font-mono">•••• {f.card?.last4}</span></span>} />
                    {f.card?.exp_month && <Row label="Expiration" value={<span className="font-mono">{f.card.exp_month}/{f.card.exp_year}</span>} />}
                    {f.card?.billing_zip && <Row label="Billing ZIP" value={<span className="font-mono">{f.card.billing_zip}</span>} />}
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-[#64748B] mb-2">Signature</p>
                    <div className="rounded-lg border border-[#E2E8F0] bg-slate-50 p-4 min-h-[90px] flex items-center">
                      {f.signature_type === "draw" && (f.signature_data || "").startsWith("data:image") ? (
                        <img src={f.signature_data} alt="signature" className="max-h-20" />
                      ) : (
                        <span className="text-3xl text-[#0F172A]" style={{ fontFamily: "Georgia, serif", fontStyle: "italic" }}>{f.signature_data}</span>
                      )}
                    </div>
                  </div>

                  {/* Audit certificate */}
                  <div className="rounded-lg border-l-4 border-[#059669] bg-[#ECFDF5] p-4">
                    <p className="text-xs font-bold uppercase tracking-wider text-[#065F46] mb-2">Audit certificate</p>
                    <div className="space-y-1.5 text-xs font-mono text-[#047857]">
                      <p className="flex items-center gap-2"><Fingerprint size={13} /> {f.audit?.verification_id}</p>
                      <p className="flex items-center gap-2"><Clock size={13} /> {f.audit?.signed_at}</p>
                      <p className="flex items-center gap-2"><Globe size={13} /> IP {f.audit?.ip || "—"}</p>
                      <p className="truncate">{(f.audit?.user_agent || "").slice(0, 70)}</p>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="rounded-xl border border-dashed border-[#E2E8F0] bg-white p-8 text-center">
                <span className="mx-auto h-11 w-11 rounded-xl bg-[#FFFBEB] flex items-center justify-center">
                  <Clock size={20} className="text-[#D97706]" />
                </span>
                <p className="mt-3 font-semibold text-[#0F172A]">Awaiting customer signature</p>
                <p className="text-sm text-[#64748B] mt-1">Share the secure link or email it to your customer.</p>
              </div>
            )}
          </div>

          {/* Share sidebar */}
          <div className="space-y-6">
            {f.locked && (
              <div data-testid="detail-locked-banner" className="rounded-xl border border-[#FDE68A] bg-[#FFFBEB] p-5">
                <div className="flex items-center gap-2 text-[#D97706]">
                  <Lock size={15} />
                  <span className="text-sm font-bold">Upgrade required</span>
                </div>
                <p className="text-xs text-[#B45309] mt-2 leading-relaxed">
                  This authorization is beyond your plan's limit, so sending and signing are paused. Upgrade or refer a business to unlock it.
                </p>
                <button onClick={() => navigate("/billing")} data-testid="detail-upgrade-btn" className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-lg bg-[#D97706] py-2 text-sm font-semibold text-white hover:bg-[#B45309] transition-colors">
                  Upgrade plan
                </button>
              </div>
            )}
            <div className="rounded-xl border border-[#E2E8F0] bg-white p-5">
              <h3 className="text-sm font-bold text-[#0F172A]">Share for signature</h3>
              <p className="text-xs text-[#64748B] mt-1 mb-3">Secure tokenized link — no customer login needed.</p>

              <div className="flex items-center gap-2 rounded-lg border border-[#E2E8F0] bg-slate-50 px-3 py-2">
                <span className="truncate text-xs font-mono text-[#475569]" data-testid="signing-link-text">{signUrl}</span>
              </div>

              <button data-testid="form-share-link-btn" onClick={copyLink} className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-lg border border-[#E2E8F0] py-2.5 text-sm font-semibold text-[#0F172A] hover:bg-slate-50 transition-colors">
                <Copy size={15} /> Copy link
              </button>

              <a data-testid="open-signing-link" href={signUrl} target="_blank" rel="noreferrer" className="mt-2 w-full inline-flex items-center justify-center gap-2 rounded-lg border border-[#E2E8F0] py-2.5 text-sm font-semibold text-[#475569] hover:bg-slate-50 transition-colors">
                <ExternalLink size={15} /> Open signing page
              </a>

              <button data-testid="form-send-email-btn" onClick={sendEmail} disabled={sending || !f.customer_email || f.locked} className="mt-2 w-full inline-flex items-center justify-center gap-2 rounded-lg bg-[#2563EB] py-2.5 text-sm font-semibold text-white hover:bg-[#1D4ED8] transition-all active:scale-[0.99] disabled:opacity-50">
                {sending ? <Loader2 size={15} className="animate-spin" /> : <Mail size={15} />}
                {f.locked ? "Upgrade to send" : f.customer_email ? "Email the link" : "No customer email"}
              </button>
              {f.email_sent_at && <p className="text-xs text-[#059669] mt-2 text-center">✓ Last emailed {new Date(f.email_sent_at).toLocaleString()}</p>}

              <button type="button" data-testid="toggle-email-msg-btn" onClick={() => setShowEmailEdit((v) => !v)} className="mt-2 text-xs font-semibold text-[#2563EB] hover:underline">
                {showEmailEdit ? "Hide message" : "Customize email message"}
              </button>
              {showEmailEdit && (
                <div className="mt-2">
                  <textarea data-testid="email-msg-input" value={emailMsg} onChange={(e) => setEmailMsg(e.target.value)} rows={4} className="w-full rounded-lg border border-[#E2E8F0] px-3 py-2 text-xs outline-none focus:border-[#2563EB] resize-none" />
                  <p className="text-[10px] text-[#94A3B8] mt-1 leading-relaxed">Placeholders: {"{business}"} {"{amount}"} {"{customer}"} {"{frequency}"}. The secure link &amp; footer are added automatically.</p>
                </div>
              )}

              <button data-testid="form-send-sms-btn" onClick={sendSms} disabled={texting || !f.customer_phone || f.locked || !smsEnabled}
                title={!smsEnabled ? "SMS delivery isn't configured yet — add Twilio credentials to enable" : ""}
                className="mt-2 w-full inline-flex items-center justify-center gap-2 rounded-lg border border-[#E2E8F0] py-2.5 text-sm font-semibold text-[#0F172A] hover:bg-slate-50 transition-colors disabled:opacity-50">
                {texting ? <Loader2 size={15} className="animate-spin" /> : <MessageSquare size={15} />}
                {!smsEnabled ? "Text the link (SMS coming soon)" : f.customer_phone ? "Text the link" : "No customer mobile"}
              </button>
              {f.sms_sent_at && <p className="text-xs text-[#059669] mt-2 text-center">✓ Last texted {new Date(f.sms_sent_at).toLocaleString()}</p>}

              {smsEnabled && (
                <>
                  <button type="button" data-testid="toggle-sms-msg-btn" onClick={() => setShowSmsEdit((v) => !v)} className="mt-2 text-xs font-semibold text-[#2563EB] hover:underline">
                    {showSmsEdit ? "Hide message" : "Customize SMS message"}
                  </button>
                  {showSmsEdit && (
                    <div className="mt-2">
                      <textarea data-testid="sms-msg-input" value={smsMsg} onChange={(e) => setSmsMsg(e.target.value)} rows={3} className="w-full rounded-lg border border-[#E2E8F0] px-3 py-2 text-xs outline-none focus:border-[#2563EB] resize-none" />
                      <p className="text-[10px] text-[#94A3B8] mt-1 leading-relaxed">Placeholders: {"{business}"} {"{amount}"} {"{customer}"} {"{frequency}"}. The signing link is appended automatically.</p>
                    </div>
                  )}
                </>
              )}
            </div>

            {!signed && (
              <div className="rounded-xl border border-[#E2E8F0] bg-white p-5" data-testid="reminder-card">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    {f.reminders_enabled !== false ? <BellRing size={15} className="text-[#2563EB]" /> : <BellOff size={15} className="text-[#94A3B8]" />}
                    <span className="text-sm font-bold text-[#0F172A]">Auto-reminders</span>
                  </div>
                  <button data-testid="reminder-toggle-btn" onClick={toggleReminders} role="switch" aria-checked={f.reminders_enabled !== false}
                    className={`relative h-6 w-11 rounded-full transition-colors ${f.reminders_enabled !== false ? "bg-[#2563EB]" : "bg-slate-300"}`}>
                    <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${f.reminders_enabled !== false ? "translate-x-5" : "translate-x-0.5"}`} />
                  </button>
                </div>
                <p className="text-xs text-[#64748B] mt-2 leading-relaxed">
                  {(f.email_sent_at || f.sms_sent_at)
                    ? `We'll re-${f.email_sent_at && f.sms_sent_at ? "email and text" : f.sms_sent_at ? "text" : "email"} the customer every 2 days until they sign (up to 3 reminders).`
                    : "Send the link first — reminders start 2 days after the first send."}
                </p>
                {f.reminder_count > 0 && (
                  <p className="text-xs text-[#059669] mt-2" data-testid="reminder-status-text">
                    {f.reminder_count} reminder{f.reminder_count > 1 ? "s" : ""} sent · last {new Date(f.last_reminder_at).toLocaleDateString()}
                  </p>
                )}
              </div>
            )}

            <div className="rounded-xl border border-[#E2E8F0] bg-[#F0F9FF] p-5">
              <div className="flex items-center gap-2 text-[#0284C7]">
                <Lock size={15} />
                <span className="text-sm font-bold">PCI-minded storage</span>
              </div>
              <p className="text-xs text-[#475569] mt-2 leading-relaxed">
                Only the last 4 digits of the card are ever stored. Full card numbers never touch our servers.
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

const Row = ({ label, value, full }) => (
  <div className={full ? "sm:col-span-2" : ""}>
    <p className="text-xs font-semibold uppercase tracking-wider text-[#94A3B8]">{label}</p>
    <p className="mt-0.5 font-semibold text-[#0F172A]">{value}</p>
  </div>
);
